"use client";

import { Suspense, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createClient } from "@/lib/supabase/client";
import { signInSchema, type SignInInput } from "@/lib/validations/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, Loader2 } from "lucide-react";
import { useLoading } from "@/lib/loading-context";

function SignInForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get("redirectTo");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInInput>({
    resolver: zodResolver(signInSchema),
  });

  const onSubmit = async (data: SignInInput) => {
    setError(null);
    setIsLoading(true);
    showLoading();

    try {
      const supabase = createClient();
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (signInError) {
        setError(signInError.message);
        hideLoading();
        return;
      }

      // Get user role and redirect accordingly
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (user) {
        // PERFORMANCE WIN: Use metadata role instead of querying DB
        // This avoids 406 errors if profile isn't ready and speeds up login
        const role = user.app_metadata?.role || user.user_metadata?.role || "clinic";
        
        // If there's a specific redirect path, use it
        if (redirectTo) {
          router.push(redirectTo);
        } else {
          router.push(`/${role}`);
        }
        
        router.refresh();
      }
    } catch {
      setError("An unexpected error occurred. Please try again.");
      hideLoading();
    } finally {
      setIsLoading(false);
      // We do NOT call hideLoading() here on success because we want the 
      // overlay to persist during the navigation transition.
      // The LoadingOverlay component handles auto-hiding on route change.
    }
  };

  return (
    <div className="glass rounded-2xl p-8 border border-white/10">
      <div className="text-center mb-8">
        <h2 className="font-heading text-xl text-studio-primary mb-2">
          Welcome Back
        </h2>
        <p className="text-studio-secondary text-sm">
          Sign in to access your portal
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="you@clinic.com"
            autoComplete="email"
            {...register("email")}
          />
          {errors.email && (
            <p className="text-red-400 text-xs mt-1">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link
              href="/auth/forgot-password"
              className="text-studio-accent text-[10px] uppercase tracking-[0.2em] hover:underline"
            >
              Forgot Password?
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            autoComplete="current-password"
            {...register("password")}
          />
          {errors.password && (
            <p className="text-red-400 text-xs mt-1">
              {errors.password.message}
            </p>
          )}
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Signing In...
            </>
          ) : (
            "Sign In"
          )}
        </Button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-studio-secondary text-sm">
          Don&apos;t have an account?{" "}
          <Link
            href="/auth/signup"
            className="text-studio-accent hover:underline"
          >
            Create Account
          </Link>
        </p>
      </div>
    </div>
  );
}

export default function SignInPage() {
  return (
    <Suspense fallback={
      <div className="glass rounded-2xl p-8 border border-white/10 flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-studio-accent mb-4" />
        <p className="text-studio-secondary text-sm animate-pulse">Loading Auth...</p>
      </div>
    }>
      <SignInForm />
    </Suspense>
  );
}

